/**
 * Steer: a person's note for the work running on a ticket (YAG-1116).
 *
 * Opened from a live run's footer. The note is stored as the ticket's note
 * for the next step (`POST /api/work-items/:id/next-step-note`, the chat's
 * `leave_next_step_note`), and Wright reads it where the next build step or
 * fix round starts; the step running now does not stop. One note waits at a
 * time, so a second replaces the first.
 *
 * Built the way Start over is (`StartOverDialog.tsx`): the body mounts only
 * while open, so every open starts clean.
 */
import { useState } from "react";

import { Btn } from "../queue/index.js";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "../ui/dialog.js";
import {
  ADD_NOTE_CONFIRM,
  ADD_NOTE_EFFECT,
  ADD_NOTE_FAILED,
  ADD_NOTE_FIELD,
  ADD_NOTE_PLACEHOLDER,
  ADD_NOTE_REPLACES,
  addNoteTitle,
} from "../work/workCopy.js";
import "./addNote.css";

/** The longest note the dialog takes; the session reads at most this much of it. */
const MAX_NOTE = 4_000;

export interface AddNoteDialogProps {
  open: boolean;
  onClose: () => void;
  ticketKey: string;
  /** Throws with the route's own sentence when the server refuses. */
  onConfirm: (body: string) => Promise<void>;
}

export function AddNoteDialog(props: AddNoteDialogProps) {
  return (
    <Dialog open={props.open} onOpenChange={(next) => (next ? undefined : props.onClose())}>
      {props.open && <AddNoteBody {...props} />}
    </Dialog>
  );
}

function AddNoteBody({ onClose, ticketKey, onConfirm }: AddNoteDialogProps) {
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasBody = body.trim() !== "";

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await onConfirm(body.trim());
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : ADD_NOTE_FAILED);
      setBusy(false);
    }
  };

  return (
    <DialogContent className="addnote" data-testid="add-note-dialog">
      <DialogTitle>{addNoteTitle(ticketKey)}</DialogTitle>
      <DialogDescription className="can">{ADD_NOTE_EFFECT}</DialogDescription>
      <label className="notefield">
        <span className="label">{ADD_NOTE_FIELD}</span>
        <textarea
          className="wk-gate-input"
          rows={3}
          maxLength={MAX_NOTE}
          value={body}
          placeholder={ADD_NOTE_PLACEHOLDER}
          aria-label={ADD_NOTE_FIELD}
          data-testid="add-note-body"
          onChange={(event) => setBody(event.target.value)}
        />
        <span className="need">{ADD_NOTE_REPLACES}</span>
      </label>
      {error && (
        <p className="err" role="alert">
          {error}
        </p>
      )}
      <div className="verbs">
        <Btn variant="quiet" disabled={busy} onClick={onClose}>
          Cancel
        </Btn>
        <Btn
          variant="primary"
          disabled={busy || !hasBody}
          onClick={() => void confirm()}
          data-testid="add-note-confirm"
        >
          {ADD_NOTE_CONFIRM}
        </Btn>
      </div>
    </DialogContent>
  );
}
